<script setup lang="ts">
import type {
  AppInputAnswers,
  AppInputDefinition,
  AppInputProblem,
  AppInputStage,
} from '~/utils/app-inputs'
import {
  appInputsAskedAt,
  isAppInputVisible,
  validateAppInputAnswers,
} from '~/utils/app-inputs'

/**
 * An app's setup questions as one form — the checkout's "Configure <app>"
 * step and the reinstall dialog both render this, so a question reads the
 * same wherever it is asked.
 *
 * - Fields appear in the order the app declares them; a field whose condition
 *   does not hold is not shown (and not sent).
 * - `advanced` fields fold under "Advanced settings": most people never open it.
 * - A problem shows once the field has been left (or changed), or once the
 *   customer tried to go on (`showAllProblems`) — never while a first answer is
 *   still being typed. A problem the API sent (`problems`) shows at once and
 *   disappears when that answer changes.
 */
const props = withDefaults(
  defineProps<{
    appCode: string
    appName: string
    definitions: AppInputDefinition[]
    modelValue: AppInputAnswers
    stage: AppInputStage
    /** Problems the API found in the last submission, by key. */
    problems?: Record<string, AppInputProblem>
    showAllProblems?: boolean
    /** Secrets the instance already holds (reinstall of the same app), by key → hint. */
    keptHints?: Record<string, string | null>
    accountEmail?: string | null
    disabled?: boolean
  }>(),
  {
    problems: () => ({}),
    showAllProblems: false,
    keptHints: () => ({}),
    accountEmail: null,
    disabled: false,
  },
)

const emit = defineEmits<{ 'update:modelValue': [value: AppInputAnswers] }>()

const touched = ref(new Set<string>())
/** API problems whose answer has changed since: no longer the answer they were about. */
const stale = ref(new Set<string>())
watch(
  () => props.problems,
  () => {
    stale.value = new Set()
  },
)

const asked = computed(() => appInputsAskedAt(props.definitions, props.stage))
const visible = computed(() =>
  asked.value.filter((def) => isAppInputVisible(def, props.definitions, props.modelValue)),
)
const basic = computed(() => visible.value.filter((def) => !def.advanced))
const advanced = computed(() => visible.value.filter((def) => def.advanced))
const advancedOpen = ref(false)

const kept = computed(() => new Set(Object.keys(props.keptHints)))
const verdict = computed(() =>
  validateAppInputAnswers(props.definitions, props.modelValue, {
    stage: props.stage,
    kept: kept.value,
    accountEmail: props.accountEmail,
  }),
)

function problemFor(key: string): AppInputProblem | null {
  const fromApi = props.problems[key]
  if (fromApi && !stale.value.has(key)) return fromApi
  return verdict.value.problems[key] ?? null
}

function showProblemFor(key: string): boolean {
  if (props.problems[key] && !stale.value.has(key)) return true
  return props.showAllProblems || touched.value.has(key)
}

const hasLaterFields = computed(() => visible.value.some((def) => def.need === 'recommended'))

// An advanced field with a problem must not hide inside a closed panel.
watch(
  () => props.showAllProblems,
  (show) => {
    if (show && advanced.value.some((def) => problemFor(def.key))) advancedOpen.value = true
  },
)

function update(key: string, value: string | number | boolean) {
  if (props.problems[key]) stale.value.add(key)
  const def = props.definitions.find((d) => d.key === key)
  // A select or a toggle is answered the moment it is clicked; text once left.
  if (def && (def.type === 'select' || def.type === 'toggle')) touched.value.add(key)
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}
</script>

<template>
  <div class="space-y-6" data-testid="app-inputs-form" :data-app="appCode">
    <p v-if="hasLaterFields" class="flex items-start gap-2 rounded-lg bg-primary/5 px-3 py-2.5 text-sm text-default">
      <UIcon name="i-heroicons-information-circle" class="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
      <span>{{ $t('appInputs.laterIntro', { app: appName }) }}</span>
    </p>

    <AppInputsField
      v-for="def in basic"
      :key="def.key"
      :app-code="appCode"
      :def="def"
      :model-value="modelValue[def.key]"
      :problem="problemFor(def.key)"
      :show-problem="showProblemFor(def.key)"
      :kept-hint="def.key in keptHints ? keptHints[def.key] : undefined"
      :account-email="accountEmail"
      :disabled="disabled"
      @update:model-value="(value) => update(def.key, value)"
      @blur="touched.add(def.key)"
    />

    <div v-if="advanced.length" class="rounded-lg border border-default">
      <button
        type="button"
        class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        :aria-expanded="advancedOpen"
        @click="advancedOpen = !advancedOpen"
      >
        <span>
          <span class="block text-sm font-medium text-highlighted">{{ $t('appInputs.advanced') }}</span>
          <span class="block text-xs text-muted">{{ $t('appInputs.advancedHint') }}</span>
        </span>
        <UIcon
          :name="advancedOpen ? 'i-heroicons-chevron-up' : 'i-heroicons-chevron-down'"
          class="size-5 shrink-0 text-muted"
          aria-hidden="true"
        />
      </button>
      <div v-show="advancedOpen" class="space-y-6 border-t border-default px-4 py-4">
        <AppInputsField
          v-for="def in advanced"
          :key="def.key"
          :app-code="appCode"
          :def="def"
          :model-value="modelValue[def.key]"
          :problem="problemFor(def.key)"
          :show-problem="showProblemFor(def.key)"
          :kept-hint="def.key in keptHints ? keptHints[def.key] : undefined"
          :account-email="accountEmail"
          :disabled="disabled"
          @update:model-value="(value) => update(def.key, value)"
          @blur="touched.add(def.key)"
        />
      </div>
    </div>
  </div>
</template>
